import { useState, useCallback, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  buildReceipt, buildKitchenTicket, buildDeliveryReceipt, buildCashReport,
  type ReceiptData, type KitchenTicketData, type DeliveryReceiptData, type CashReportData, type PrinterSize,
  PAYMENT_LABELS, ORDER_TYPE_LABELS,
} from "@/lib/escpos";

interface PrinterState {
  connected: boolean;
  printerName: string | null;
  size: PrinterSize;
}

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

export function isWebSerialSupported(): boolean {
  return "serial" in navigator;
}

export function useThermalPrinter() {
  const { toast } = useToast();
  const portRef = useRef<any>(null);
  const writerRef = useRef<any>(null);
  const [state, setState] = useState<PrinterState>({ connected: false, printerName: null, size: "80mm" });

  const setSize = useCallback((size: PrinterSize) => {
    setState(prev => ({ ...prev, size }));
  }, []);

  const connect = useCallback(async (): Promise<boolean> => {
    if (!isWebSerialSupported()) {
      toast({ title: "Navegador não suportado", description: "Use o Google Chrome para conectar à impressora térmica via USB.", variant: "destructive" });
      return false;
    }
    try {
      const port = await (navigator as any).serial.requestPort({
        filters: [
          { usbVendorId: 0x0483 }, { usbVendorId: 0x0416 }, { usbVendorId: 0x04b8 },
          { usbVendorId: 0x0dd4 }, { usbVendorId: 0x0fe6 }, { usbVendorId: 0x20d1 }, { usbVendorId: 0x1fc9 },
        ],
      });
      await port.open({ baudRate: 9600 });
      const writer = port.writable?.getWriter();
      if (!writer) throw new Error("Não foi possível abrir stream de escrita");
      portRef.current = port;
      writerRef.current = writer;
      const info = port.getInfo();
      const name = info.usbProductId
        ? `Impressora (USB ${info.usbVendorId?.toString(16)}:${info.usbProductId?.toString(16)})`
        : "Impressora Térmica USB";
      setState(prev => ({ ...prev, connected: true, printerName: name }));
      toast({ title: "🖨️ Impressora conectada!", description: name });
      return true;
    } catch (error: any) {
      if (error.name !== "NotFoundError") {
        toast({ title: "Erro ao conectar", description: error.message || "Não foi possível conectar à impressora.", variant: "destructive" });
      }
      return false;
    }
  }, [toast]);

  const disconnect = useCallback(async () => {
    try {
      if (writerRef.current) { writerRef.current.releaseLock(); writerRef.current = null; }
      if (portRef.current) { await portRef.current.close(); portRef.current = null; }
      setState(prev => ({ ...prev, connected: false, printerName: null }));
      toast({ title: "Impressora desconectada" });
    } catch (error) { console.error("Error disconnecting printer:", error); }
  }, [toast]);

  const sendRaw = useCallback(async (data: Uint8Array): Promise<boolean> => {
    if (!writerRef.current) return false;
    try {
      await writerRef.current.write(data);
      return true;
    } catch (error) {
      console.error("Print error:", error);
      toast({ title: "Erro ao imprimir", description: "Verifique a conexão da impressora.", variant: "destructive" });
      setState(prev => ({ ...prev, connected: false, printerName: null }));
      writerRef.current = null;
      portRef.current = null;
      return false;
    }
  }, [toast]);

  // ===== FALLBACK: Window Print =====
  const fallbackPrintHtml = useCallback((html: string) => {
    const w = window.open("", "_blank", "width=350,height=600");
    if (!w) { toast({ title: "Pop-up bloqueado", description: "Permita pop-ups para imprimir.", variant: "destructive" }); return; }
    w.document.write(html);
    w.document.close();
  }, [toast]);

  // ===== HTML Builders =====

  const buildItemAddonsHtml = (item: { addons?: { categoryName?: string; name: string; quantity: number; price: number }[] }) => {
    if (!item.addons || item.addons.length === 0) return "";
    return item.addons.map(a => {
      const price = a.price > 0 ? ` <span style="color:#666">+${formatCurrency(a.price * a.quantity)}</span>` : "";
      return `<div style="margin-left:20px;font-size:11px;color:#555">+ ${a.quantity > 1 ? `${a.quantity}x ` : ""}${a.name}${price}</div>`;
    }).join("");
  };

  const buildKitchenHtml = (data: KitchenTicketData) => {
    const isFirstTime = (data.customer.totalOrders ?? 0) === 0;
    const itemsHtml = data.items.map(i =>
      `<div class="item"><span class="qty">${i.quantity}x</span> ${i.name}${buildItemAddonsHtml(i)}${i.notes ? `<br><span class="note">→ ${i.notes}</span>` : ""}</div>`
    ).join("");
    const payStatus = data.paymentStatus === "paid" ? "PAGO" : "NAO PAGO";

    return `<!DOCTYPE html><html><head><title>Cozinha</title>
    <style>
      body{font-family:'Courier New',monospace;width:300px;margin:0 auto;padding:10px;font-size:14px}
      h2{text-align:center;margin:0 0 4px}
      .sep{border-top:1px dashed #000;margin:8px 0}
      .item{padding:4px 0;border-bottom:1px dotted #ccc;font-size:16px;font-weight:bold}
      .qty{font-size:18px;color:#333}
      .note{font-size:12px;font-weight:normal;color:#666;margin-left:20px}
      .center{text-align:center}
      .first-time{background:#fff3cd;border:2px solid #ffc107;padding:6px;text-align:center;font-weight:bold;margin:4px 0}
      .info{font-size:12px;color:#555}
    </style></head><body>
      <h2>** COZINHA **</h2>
      <div class="sep"></div>
      <p><strong>PEDIDO #${data.orderId.slice(0, 8).toUpperCase()}</strong></p>
      <p>${new Date(data.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
      <p>Tipo: ${ORDER_TYPE_LABELS[data.orderType] || data.orderType}${data.tableNumber ? ` ${data.tableNumber}` : ""}</p>
      ${data.customer.name ? `<p>Cliente: ${data.customer.name}</p>` : ""}
      ${data.waiterName ? `<p>Garçom: ${data.waiterName}</p>` : ""}
      ${data.scheduledFor ? `<p><strong>AGENDADO: ${new Date(data.scheduledFor).toLocaleString("pt-BR")}</strong></p>` : ""}
      <p class="info">Pagamento: ${payStatus}${data.paymentMethod ? ` (${PAYMENT_LABELS[data.paymentMethod] || data.paymentMethod})` : ""}</p>
      ${isFirstTime ? '<div class="first-time">⭐ PRIMEIRA COMPRA DO CLIENTE ⭐</div>' : (data.customer.totalOrders && data.customer.totalOrders > 0 ? `<p class="info">Cliente recorrente (${data.customer.totalOrders} pedidos)</p>` : "")}
      <div class="sep"></div>
      ${itemsHtml}
      ${data.notes ? `<div class="sep"></div><p><strong>OBS: ${data.notes}</strong></p>` : ""}
      <div class="sep"></div>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}</script>
    </body></html>`;
  };

  const buildReceiptHtml = (data: ReceiptData) => {
    const isFirstTime = (data.customer.totalOrders ?? 0) === 0;
    const itemsHtml = data.items.map(i =>
      `<tr><td>${i.quantity}x ${i.name}</td><td style="text-align:right">${formatCurrency(i.totalPrice)}</td></tr>${buildItemAddonsHtml(i)}`
    ).join("");

    const changeHtml = data.changeAmount && data.changeAmount > 0
      ? `<div class="sep"></div><p class="bold">TROCO: ${formatCurrency(data.changeAmount)}</p>` : "";

    return `<!DOCTYPE html><html><head><title>Cupom</title>
    <style>
      body{font-family:'Courier New',monospace;width:300px;margin:0 auto;padding:10px;font-size:12px}
      h2{text-align:center;margin:0 0 4px}
      .sep{border-top:1px dashed #000;margin:6px 0}
      table{width:100%;border-collapse:collapse}
      td{padding:2px 0}
      .center{text-align:center}
      .total{font-size:16px;font-weight:bold}
      .bold{font-weight:bold}
      .first-time{background:#fff3cd;border:2px solid #ffc107;padding:4px;text-align:center;font-weight:bold;margin:4px 0;font-size:11px}
      .info{font-size:11px;color:#555}
    </style></head><body>
      <h2>${data.restaurant.name}</h2>
      ${data.restaurant.phone ? `<p class="center">${data.restaurant.phone}</p>` : ""}
      ${data.restaurant.cnpj ? `<p class="center">CNPJ: ${data.restaurant.cnpj}</p>` : ""}
      ${data.restaurant.address ? `<p class="center" style="font-size:10px">${data.restaurant.address}</p>` : ""}
      <div class="sep"></div>
      <p class="bold">PEDIDO #${data.orderId.slice(0, 8).toUpperCase()}</p>
      <p>Data: ${new Date(data.createdAt).toLocaleString("pt-BR")}</p>
      <p>Tipo: ${ORDER_TYPE_LABELS[data.orderType] || data.orderType}${data.tableNumber ? ` ${data.tableNumber}` : ""}</p>
      ${data.customer.name ? `<p>Cliente: ${data.customer.name}</p>` : ""}
      ${data.customer.phone && data.orderType === "delivery" ? `<p>Tel: ${data.customer.phone}</p>` : ""}
      ${data.waiterName ? `<p>Garçom: ${data.waiterName}</p>` : ""}
      ${data.ifoodReference ? `<p>iFood Ref: ${data.ifoodReference}</p>` : ""}
      ${data.scheduledFor ? `<p><strong>AGENDADO: ${new Date(data.scheduledFor).toLocaleString("pt-BR")}</strong></p>` : ""}
      ${isFirstTime ? '<div class="first-time">⭐ PRIMEIRA COMPRA DO CLIENTE ⭐</div>' : (data.customer.totalOrders && data.customer.totalOrders > 0 ? `<p class="info">Cliente recorrente (${data.customer.totalOrders} pedidos)</p>` : "")}
      ${data.customer.loyaltyPoints && data.customer.loyaltyPoints > 0 ? `<p class="info">Pontos acumulados: ${data.customer.loyaltyPoints}</p>` : ""}
      ${data.orderType === "delivery" && data.deliveryAddress ? `<div class="sep"></div><p class="bold">ENDEREÇO:</p><p>${data.deliveryAddress}</p>` : ""}
      <div class="sep"></div>
      <table>${itemsHtml}</table>
      <div class="sep"></div>
      <table>
        <tr><td>Subtotal</td><td style="text-align:right">${formatCurrency(data.subtotal)}</td></tr>
        ${(data.deliveryFee ?? 0) > 0 ? `<tr><td>Taxa entrega</td><td style="text-align:right">${formatCurrency(data.deliveryFee!)}</td></tr>` : ""}
        ${(data.discount ?? 0) > 0 ? `<tr><td>Desconto</td><td style="text-align:right">-${formatCurrency(data.discount!)}</td></tr>` : ""}
        <tr class="total"><td>TOTAL</td><td style="text-align:right">${formatCurrency(data.total)}</td></tr>
      </table>
      <div class="sep"></div>
      <p>Pagamento: ${PAYMENT_LABELS[data.paymentMethod || ""] || data.paymentMethod || "---"}</p>
      ${data.paymentStatus ? `<p>Status: ${data.paymentStatus === "paid" ? "PAGO" : "PENDENTE"}</p>` : ""}
      ${changeHtml}
      <div class="sep"></div>
      <p class="center">Obrigado pela preferência!</p>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}</script>
    </body></html>`;
  };

  const buildDeliveryHtml = (data: DeliveryReceiptData) => {
    const isFirstTime = (data.customer.totalOrders ?? 0) === 0;
    const itemsHtml = data.items.map(i =>
      `<tr><td>${i.quantity}x ${i.name}</td><td style="text-align:right">${formatCurrency(i.totalPrice)}</td></tr>${buildItemAddonsHtml(i)}`
    ).join("");

    const changeHtml = data.changeAmount && data.changeAmount > 0
      ? `<div class="sep"></div><p class="bold">TROCO: ${formatCurrency(data.changeAmount)}</p>` : "";

    return `<!DOCTYPE html><html><head><title>Entrega</title>
    <style>
      body{font-family:'Courier New',monospace;width:300px;margin:0 auto;padding:10px;font-size:12px}
      h2{text-align:center;margin:0 0 4px}
      .sep{border-top:1px dashed #000;margin:6px 0}
      table{width:100%;border-collapse:collapse}
      td{padding:2px 0}
      .center{text-align:center}
      .total{font-size:16px;font-weight:bold}
      .bold{font-weight:bold}
      .first-time{background:#fff3cd;border:2px solid #ffc107;padding:4px;text-align:center;font-weight:bold;margin:4px 0;font-size:11px}
      .info{font-size:11px;color:#555}
      .thanks{margin-top:8px;font-style:italic;font-size:13px}
    </style></head><body>
      <h2>${data.restaurant.name}</h2>
      ${data.restaurant.phone ? `<p class="center">${data.restaurant.phone}</p>` : ""}
      ${data.restaurant.cnpj ? `<p class="center">CNPJ: ${data.restaurant.cnpj}</p>` : ""}
      ${data.restaurant.address ? `<p class="center" style="font-size:10px">${data.restaurant.address}</p>` : ""}
      <div class="sep"></div>
      <p class="bold">PEDIDO #${data.orderId.slice(0, 8).toUpperCase()}</p>
      <p>Data: ${new Date(data.createdAt).toLocaleString("pt-BR")}</p>
      <p>Tipo: Delivery</p>
      ${data.customer.name ? `<p>Cliente: ${data.customer.name}</p>` : ""}
      ${data.customer.phone ? `<p>Tel: ${data.customer.phone}</p>` : ""}
      ${data.ifoodReference ? `<p>iFood Ref: ${data.ifoodReference}</p>` : ""}
      ${data.scheduledFor ? `<p><strong>AGENDADO: ${new Date(data.scheduledFor).toLocaleString("pt-BR")}</strong></p>` : ""}
      ${isFirstTime ? '<div class="first-time">⭐ PRIMEIRA COMPRA DO CLIENTE ⭐</div>' : (data.customer.totalOrders && data.customer.totalOrders > 0 ? `<p class="info">Cliente recorrente (${data.customer.totalOrders} pedidos)</p>` : "")}
      ${data.customer.loyaltyPoints && data.customer.loyaltyPoints > 0 ? `<p class="info">Pontos acumulados: ${data.customer.loyaltyPoints}</p>` : ""}
      ${data.deliveryAddress ? `<div class="sep"></div><p class="bold">ENDEREÇO DE ENTREGA:</p><p>${data.deliveryAddress}</p>` : ""}
      <div class="sep"></div>
      <table>${itemsHtml}</table>
      <div class="sep"></div>
      <table>
        <tr><td>Subtotal</td><td style="text-align:right">${formatCurrency(data.subtotal)}</td></tr>
        ${(data.deliveryFee ?? 0) > 0 ? `<tr><td>Taxa entrega</td><td style="text-align:right">${formatCurrency(data.deliveryFee!)}</td></tr>` : ""}
        ${(data.discount ?? 0) > 0 ? `<tr><td>Desconto</td><td style="text-align:right">-${formatCurrency(data.discount!)}</td></tr>` : ""}
        <tr class="total"><td>TOTAL</td><td style="text-align:right">${formatCurrency(data.total)}</td></tr>
      </table>
      <div class="sep"></div>
      <p>Pagamento: ${PAYMENT_LABELS[data.paymentMethod || ""] || data.paymentMethod || "---"}</p>
      ${data.paymentStatus ? `<p>Status: ${data.paymentStatus === "paid" ? "PAGO" : "PENDENTE"}</p>` : ""}
      ${changeHtml}
      ${data.restaurant.receiptThankYouMessage ? `<div class="sep"></div><p class="center thanks">${data.restaurant.receiptThankYouMessage}</p>` : ""}
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}</script>
    </body></html>`;
  };

  const buildCashReportHtml = (data: CashReportData) => {
    return `<!DOCTYPE html><html><head><title>Fechamento de Caixa</title>
    <style>
      body{font-family:'Courier New',monospace;width:300px;margin:0 auto;padding:10px;font-size:12px}
      h2{text-align:center;margin:0 0 4px}
      .sep{border-top:1px dashed #000;margin:6px 0}
      table{width:100%;border-collapse:collapse}
      td{padding:2px 0}
      .center{text-align:center}
      .bold{font-weight:bold}
      .neg{color:red}
    </style></head><body>
      <h2>${data.restaurantName}</h2>
      <h3 class="center">FECHAMENTO DE CAIXA</h3>
      <div class="sep"></div>
      <p>Abertura: ${data.openedAt}</p>
      <p>Fechamento: ${data.closedAt}</p>
      <div class="sep"></div>
      <table>
        <tr><td>Saldo Inicial</td><td style="text-align:right">${formatCurrency(data.openingBalance)}</td></tr>
        <tr><td>Vendas (${data.salesCount})</td><td style="text-align:right">+${formatCurrency(data.totalSales)}</td></tr>
        <tr><td>Suprimentos</td><td style="text-align:right">+${formatCurrency(data.totalSuprimento)}</td></tr>
        <tr><td>Sangrias</td><td style="text-align:right">-${formatCurrency(data.totalSangria)}</td></tr>
        <tr><td>Trocos</td><td style="text-align:right">-${formatCurrency(data.totalChangeGiven)}</td></tr>
      </table>
      <div class="sep"></div>
      <table>
        <tr class="bold"><td>Saldo Esperado</td><td style="text-align:right">${formatCurrency(data.expectedBalance)}</td></tr>
        <tr class="bold"><td>Saldo Informado</td><td style="text-align:right">${formatCurrency(data.closingBalance)}</td></tr>
        <tr class="bold"><td>Diferença</td><td style="text-align:right${data.difference < 0 ? ";color:red" : ""}">${data.difference >= 0 ? "+" : ""}${formatCurrency(data.difference)}</td></tr>
      </table>
      <div class="sep"></div>
      <p class="center">Relatório gerado automaticamente</p>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),500)}</script>
    </body></html>`;
  };

  // ===== HIGH-LEVEL PRINT FUNCTIONS =====

  const printReceipt = useCallback(async (data: ReceiptData) => {
    if (state.connected) {
      const bytes = buildReceipt(data, state.size);
      const success = await sendRaw(bytes);
      if (success) return;
    }
    fallbackPrintHtml(buildReceiptHtml(data));
  }, [state.connected, state.size, sendRaw, fallbackPrintHtml]);

  const printKitchenTicket = useCallback(async (data: KitchenTicketData) => {
    if (state.connected) {
      const bytes = buildKitchenTicket(data, state.size);
      const success = await sendRaw(bytes);
      if (success) return;
    }
    fallbackPrintHtml(buildKitchenHtml(data));
  }, [state.connected, state.size, sendRaw, fallbackPrintHtml]);

  const printDeliveryReceipt = useCallback(async (data: DeliveryReceiptData) => {
    if (state.connected) {
      const bytes = buildDeliveryReceipt(data, state.size);
      const success = await sendRaw(bytes);
      if (success) return;
    }
    fallbackPrintHtml(buildDeliveryHtml(data));
  }, [state.connected, state.size, sendRaw, fallbackPrintHtml]);

  const printCashReport = useCallback(async (data: CashReportData) => {
    if (state.connected) {
      const bytes = buildCashReport(data, state.size);
      const success = await sendRaw(bytes);
      if (success) return;
    }
    fallbackPrintHtml(buildCashReportHtml(data));
  }, [state.connected, state.size, sendRaw, fallbackPrintHtml]);

  const openCashDrawer = useCallback(async () => {
    if (state.connected) {
      const { CMD } = await import("@/lib/escpos");
      await sendRaw(CMD.OPEN_DRAWER);
    }
  }, [state.connected, sendRaw]);

  return {
    ...state,
    connect,
    disconnect,
    setSize,
    printReceipt,
    printKitchenTicket,
    printDeliveryReceipt,
    printCashReport,
    openCashDrawer,
    sendRaw,
    isSupported: isWebSerialSupported(),
  };
}
